import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  InfoCircleOutlined,
  LoadingOutlined,
  WarningFilled,
} from '@ant-design/icons';
import { Select, Tooltip, Typography } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  EmailVerifyResult,
  EmailVerifyStatus,
  SignatoryRole,
  WorkflowStep,
  WorkflowTemplate,
} from '@/api/workflows';
import { workflowsApi } from '@/api/workflows';
import { useSession } from '@/auth/useSession';

import { evaluateConditions } from './evaluateConditions';
import type { FieldValue } from './FieldRenderer';
import { getInstanceCount, qualifiedKey, sectionMinInstances } from './sectionInstances';

const { Title, Paragraph, Text } = Typography;

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/** The two modes BMO offers per signatory. */
export const ESIGN_MODES = ['remote', 'in-person'] as const;
export type ESignMode = (typeof ESIGN_MODES)[number];

/**
 * Value key holding one signatory's chosen eSign mode.
 *
 * Namespaced so it can never collide with a real field id (those are GUIDs),
 * and instance-qualified so a role mapped into a REPEATABLE section gets one
 * choice per signer rather than one for the whole role — the same bug class
 * that has bitten signer resolution, reveals and review before now.
 */
export function esignModeKey(roleSlug: string, instanceIdx: number): string {
  return `esignMode:${roleSlug}:${instanceIdx}`;
}

/** The stored mode for one signatory, or null when nothing valid is set. */
export function readESignMode(
  values: Record<string, FieldValue>,
  roleSlug: string,
  instanceIdx: number,
): ESignMode | null {
  const raw = values[esignModeKey(roleSlug, instanceIdx)];
  return typeof raw === 'string' && ESIGN_MODES.includes(raw as ESignMode)
    ? (raw as ESignMode)
    : null;
}

/**
 * Value key holding the advisor's chosen representative code for this signing
 * package.
 *
 * Same namespacing + instance-qualification rationale as {@link esignModeKey}:
 * the store is shared with real field values (GUID keys), and a role mapped
 * into a repeatable section is one signer per instance.
 */
export function esignRepCodeKey(roleSlug: string, instanceIdx: number): string {
  return `esignRepCode:${roleSlug}:${instanceIdx}`;
}

/**
 * The stored representative code for one signatory, or null when nothing valid
 * is set.
 *
 * Validated against the codes the session actually carries — an advisor whose
 * GAMX profile changed between sessions must not keep filing under a code that
 * is no longer theirs, and a stale answer left behind in the value store must
 * not read as a live choice.
 */
export function readESignRepCode(
  values: Record<string, FieldValue>,
  roleSlug: string,
  instanceIdx: number,
  allowed: readonly string[],
): string | null {
  const raw = values[esignRepCodeKey(roleSlug, instanceIdx)];
  return typeof raw === 'string' && allowed.includes(raw) ? raw : null;
}

/**
 * Stand-in dealer / representative codes for `mode='preview'`.
 *
 * Preview is driven by an ORIGINATOR or APPROVER session, which carries no GAMX
 * advisor profile at all (see MeResponse) — so without a fallback both new
 * columns render as em dashes and the originator can't tell the rep-code picker
 * from a broken cell. Preview is already a sample-data surface (PreviewPage
 * seeds `initialValues` the same way), so sample codes here are consistent with
 * how the rest of the step previews.
 *
 * NEVER used in `mode='advisor'` — a real advisor either has codes from GAMX or
 * never got a session at all (login is blocked on zero codes, see
 * AuthEndpoints), so a fabricated code could not silently reach OneSpan.
 */
export const PREVIEW_ADVISOR_CODES = {
  dealerCode: '1249',
  representativeCodes: ['CD5678', 'EF9012', 'GH3456'],
} as const;

/**
 * The advisor's dealer code + selectable representative codes for this render.
 *
 * Real session wins whenever it carries either value. The preview fallback only
 * fires when the session has NEITHER — i.e. a non-advisor session driving the
 * preview surfaces.
 */
export function resolveAdvisorCodes(
  user: { dealerCode?: string | null; representativeCodes?: string[] | null } | null | undefined,
  mode: 'preview' | 'advisor',
): { dealerCode: string | null; representativeCodes: string[] } {
  const representativeCodes = user?.representativeCodes ?? [];
  const dealerCode = user?.dealerCode ?? null;
  if (representativeCodes.length > 0 || dealerCode) return { dealerCode, representativeCodes };
  if (mode === 'preview') {
    return {
      dealerCode: PREVIEW_ADVISOR_CODES.dealerCode,
      representativeCodes: [...PREVIEW_ADVISOR_CODES.representativeCodes],
    };
  }
  return { dealerCode: null, representativeCodes: [] };
}

/**
 * How many signatories still have no mode chosen. Zero means the advisor may
 * proceed — the ticket's "when a mode for esign is selected for all client
 * signatories, advisor should be able to generate initiate esign".
 *
 * Counts the advisor too: they select a mode for themselves.
 */
export function countSignersMissingMode(
  signers: { role: { slug: string }; instanceIdx: number }[],
  values: Record<string, FieldValue>,
): number {
  return signers.filter(
    ({ role, instanceIdx }) => readESignMode(values, role.slug, instanceIdx) === null,
  ).length;
}

/**
 * Read-only preview of the SIGNATORY package BMO will ship to OneSpan once
 * the advisor finishes filling the form. Resolves each signer's contact info
 * from the originator's authored config:
 *   - Applicant: pulls Name / Email / Phone values from the mapped form
 *     fields (so the originator can verify in preview that the right
 *     person's data flows into the SIGNATORY payload).
 *   - Advisor: pulls from the BFF/JWT session (name + email from /me).
 * Per-PDF signature placements are then listed below — same shape as the
 * originator authored in the OneSpanConfig drawer.
 *
 * No interactive controls: this is a runtime preview, not the actual
 * signing surface (BMO + OneSpan own that downstream). The advisor
 * clicks "Next" to move past it.
 */
export function OneSpanESignStep({
  template,
  step,
  values,
  language,
  mode = 'advisor',
  onSetStepBlocker,
  onChange,
}: {
  template: WorkflowTemplate;
  step: WorkflowStep;
  values: Record<string, FieldValue>;
  language: 'en' | 'fr';
  /** In 'preview' we still show the resolved signatory list, but no
   *  ZeroBounce call fires — preview isn't a real advisor flow and would
   *  burn credits on every dashboard-preview click. */
  mode?: 'preview' | 'advisor';
  /** Set by FormShell — the OneSpan step blocks Next when any signatory
   *  email fails ZeroBounce verification (INFLOWS-188). Called with a
   *  tooltip string when blocking; called with null on unmount. */
  onSetStepBlocker?: (reason: string | null) => void;
  /** Writes the per-signatory eSign mode back into FormShell's value store.
   *  The signatory CONTACT rows stay read-only per the ticket ("non editable
   *  from the summary page") — the mode selector is the one control here. */
  onChange?: (key: string, next: FieldValue) => void;
}) {
  const { t } = useTranslation();
  const { user } = useSession();

  // Originator-authored step title + description. Bold title sits flush at
  // the top to match the other step renderers (Application Review etc.);
  // description is a sub-line in slate-700.
  const title = language === 'fr' ? step.titleFr : step.titleEn;
  const description = language === 'fr' ? step.descriptionFr : step.descriptionEn;

  const config = step.oneSpanConfig;

  // Gating conditions decide whether a signer is included in the signing
  // package — same evaluator the form renderer uses for step/section/field
  // visibility. Advisor has no condition UI and is always included.
  // Memoized: `signers` and `resolvedEmails` below both depend on this, and a
  // fresh array every render made those memos no-ops.
  const visibleRoles = useMemo(
    () =>
      (config?.roles ?? []).filter(
        (role) =>
          role.type === 'advisor' ||
          evaluateConditions(role.conditions, role.conditionCombinator, values),
      ),
    [config, values],
  );

  // Where each authored field lives, so a role mapped into a REPEATABLE
  // section can be expanded into one signer per instance. Without this the
  // resolver reads the bare `values[fieldId]`, which only ever exists for
  // instance 0 — i.e. a second Accountholder never reached the signing
  // package at all.
  const sectionByField = useMemoFieldSections(template);

  // One entry per (role, instance). Non-repeatable roles produce a single
  // entry with instance 0, so nothing changes for them.
  const signers = useMemo(
    () =>
      visibleRoles.flatMap((role) => {
        const count = signerInstanceCount(role, sectionByField, values);
        return Array.from({ length: count }, (_, instanceIdx) => ({ role, instanceIdx }));
      }),
    [visibleRoles, sectionByField, values],
  );

  // Advisor dealer code + the rep codes they may file this application under.
  // Falls back to sample codes in preview, where the signed-in user is an
  // originator/approver and carries no GAMX profile.
  const advisorCodes = useMemo(() => resolveAdvisorCodes(user, mode), [user, mode]);

  // Auto-select when the advisor carries exactly ONE representative code —
  // the same rule the `advisor.repCode` field prefill follows (see
  // advisorPrefill.resolveAdvisorSourceValue): with one code there is no choice
  // to make, so don't make them make it; with several the Select stays empty
  // until they pick, because choosing for them would silently file the
  // application under a code they never saw.
  const soleRepCode =
    advisorCodes.representativeCodes.length === 1 ? advisorCodes.representativeCodes[0] : null;
  useEffect(() => {
    if (!onChange || !soleRepCode) return;
    for (const { role, instanceIdx } of signers) {
      if (role.type !== 'advisor') continue;
      if (readESignRepCode(values, role.slug, instanceIdx, advisorCodes.representativeCodes)) {
        continue;
      }
      onChange(esignRepCodeKey(role.slug, instanceIdx), soleRepCode);
    }
    // `values` is read for the "already set" guard only — putting it in deps
    // would re-run this on every keystroke anywhere in the form. The effect
    // needs to fire when the SIGNER LIST or the codes change, which is exactly
    // what's listed; the guard then makes a redundant run a no-op anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signers, soleRepCode, onChange]);

  // Every signatory — client AND advisor — picks a mode for themselves, per
  // the ticket ("BMO Advisor should be allowed to select a mode of esign for
  // self"). Counted here rather than inside the render so the Next-blocker
  // effect below can depend on a primitive.
  const missingModeCount = useMemo(
    () => countSignersMissingMode(signers, values),
    [signers, values],
  );

  // Resolve the email address for every visible signatory ONCE per render
  // so both the SignerRow render and the verify effect below see the same
  // list.
  //
  // DELIBERATE: verification is NOT conditioned on the chosen eSign mode.
  // Confirmed by BMO 2026-07-30 — "in person or remote we need verify that
  // email always". An in-person signer still needs a deliverable address for
  // the signer record and audit trail, so do not add a `mode === 'remote'`
  // short-circuit here as an optimisation.
  //
  // Emails include the advisor's session email — advisors mistype
  // their own account email far less often, but ZeroBounce catches it if
  // the whole account is bad. Empty session emails (some dev-login paths)
  // are coerced to null so the resolver treats them as absent — a real
  // Entra session always carries a non-empty address.
  const advisorEmail = user?.email && user.email.trim() !== '' ? user.email : null;
  const resolvedEmails = useMemo(() => {
    const out: string[] = [];
    for (const { role, instanceIdx } of signers) {
      const resolved = resolveSignerContact(
        role,
        values,
        sectionByField,
        instanceIdx,
        null,
        advisorEmail,
        t,
      );
      if (resolved.email) out.push(resolved.email.trim().toLowerCase());
    }
    return Array.from(new Set(out)); // dedupe — same email in two roles = one credit
  }, [signers, values, sectionByField, advisorEmail, t]);

  // Verify effect. Fires on step entry (mount) and re-fires when the
  // resolved emails change (advisor edited a mapped form field on a prior
  // step). Per-email cache lives in a ref so a re-visit doesn't burn
  // credits on values we've already checked this session.
  const [verifyResults, setVerifyResults] = useState<Record<string, EmailVerifyResult>>({});
  const [verifying, setVerifying] = useState(false);
  const cacheRef = useRef<Record<string, EmailVerifyResult>>({});

  // Stable string key for the effect deps — resolvedEmails is a new
  // array reference every render (visibleRoles / signers aren't
  // referentially stable), which would fire this effect on every render
  // and hammer the endpoint into a 429 rate-limit trip. Comparing the
  // JOINED value string dedupes those spurious renders — the effect
  // only fires when the actual SET of emails changes.
  const resolvedEmailsKey = resolvedEmails.join('|');

  useEffect(() => {
    // Empty email set (no mapped fields yet) → nothing to check.
    // Preview mode ALSO fires ZeroBounce because that's how we test the
    // integration today — real advisor mode isn't wired in the FE yet.
    // Per-email cacheRef still prevents credit re-spending on step
    // re-visits, so the cost stays bounded.
    if (resolvedEmails.length === 0) return;

    const toVerify = resolvedEmails.filter((e) => !cacheRef.current[e]);
    // Preload the state from the cache so a re-mount shows previous results
    // instantly while any newly-added emails are still in flight.
    const initial: Record<string, EmailVerifyResult> = {};
    for (const e of resolvedEmails) {
      if (cacheRef.current[e]) initial[e] = cacheRef.current[e];
    }
    setVerifyResults(initial);
    if (toVerify.length === 0) return;

    let cancelled = false;
    setVerifying(true);
    workflowsApi
      .verifyEmails(toVerify)
      .then((results) => {
        if (cancelled) return;
        for (const r of results) cacheRef.current[r.email.trim().toLowerCase()] = r;
        setVerifyResults((prev) => {
          const next = { ...prev };
          for (const r of results) next[r.email.trim().toLowerCase()] = r;
          return next;
        });
      })
      .finally(() => {
        if (!cancelled) setVerifying(false);
      });

    return () => {
      cancelled = true;
    };
    // resolvedEmailsKey is the stable dep — resolvedEmails is read inside
    // via closure but only iterated within the effect body, so we don't
    // need it in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, resolvedEmailsKey]);

  // Report the Next blocker to FormShell. Blocks when:
  //   1. Verification is in flight for any resolved email (missing result
  //      OR the shared verifying spinner is on) — closes the race where
  //      an advisor could click Next before ZeroBounce settles and slip
  //      past the AC's hard-block. Unknown-returned rows are NOT blocked
  //      (that's the intentional soft-degrade path when ZeroBounce is
  //      unreachable).
  //   2. Any resolved email came back Invalid — the AC's hard-block.
  // CatchAll / SpamTrap / DoNotMail render as yellow "warn" pills but
  // don't block per the AC — BMO explicitly asked only Invalid to gate.
  useEffect(() => {
    if (!onSetStepBlocker) return;
    const anyPending =
      resolvedEmails.length > 0 && (verifying || resolvedEmails.some((e) => !verifyResults[e]));
    if (anyPending) {
      onSetStepBlocker(
        t('formRenderer.eSign.emailVerify.checking', {
          defaultValue: 'Checking signatory emails…',
        }),
      );
      return;
    }
    const invalid = resolvedEmails
      .map((e) => verifyResults[e])
      .filter((r): r is EmailVerifyResult => !!r && r.status === 'Invalid');
    if (invalid.length === 0) {
      // "When a mode for esign is selected for all client signatories, advisor
      // should be able to generate initiate esign" — until then Next stays
      // disabled. Checked AFTER the email gate so the more specific email
      // message wins when both apply.
      if (missingModeCount > 0) {
        onSetStepBlocker(
          t('formRenderer.eSign.mode.nextBlocked', {
            count: missingModeCount,
            defaultValue: 'Select an eSign mode for every signatory before continuing.',
          }),
        );
        return;
      }
      onSetStepBlocker(null);
    } else {
      onSetStepBlocker(
        t('formRenderer.eSign.emailVerify.nextBlocked', {
          count: invalid.length,
          defaultValue: 'Fix the invalid signatory email(s) before continuing.',
        }),
      );
    }
    // Only clear on unmount — an in-place cleanup would flicker the
    // blocker off/on during every result-arrival tick, giving a fast
    // clicker a one-render window where Next is enabled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSetStepBlocker, mode, resolvedEmailsKey, verifyResults, verifying, missingModeCount, t]);

  // Dedicated unmount cleanup so the step-blocker is released when the
  // advisor navigates away — kept separate from the setter-effect above
  // so a mid-render dep change doesn't accidentally trigger a stale null.
  useEffect(() => {
    return () => {
      if (onSetStepBlocker) onSetStepBlocker(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Title level={3} style={{ margin: 0, fontWeight: 700, color: '#111827' }}>
        {title}
      </Title>
      {description && (
        <Paragraph style={{ marginBottom: 0, color: '#374151', fontSize: 14 }}>
          {description}
        </Paragraph>
      )}

      {!config || config.roles.length === 0 ? (
        <EmptyCard message={t('formRenderer.eSign.notConfigured')} />
      ) : (
        <>
          {/* 2026-07-27 BMO design: a striped table, one row per signatory —
              Name / Email / Mobile Phone Number / eSign Type — replacing the
              bordered per-signer cards. The contact resolution, the ZeroBounce
              verdict and the Next-blocking behaviour are unchanged; only the
              presentation moved. */}
          <div className="bmo-esign-table" role="table">
            <div className="bmo-esign-table__head" role="row">
              <span role="columnheader">{t('formRenderer.eSign.colName')}</span>
              <span role="columnheader">
                {t('formRenderer.eSign.colEmail')}
                <Tooltip title={t('formRenderer.eSign.colEmailHelp')}>
                  <InfoCircleOutlined className="bmo-esign-table__info" />
                </Tooltip>
              </span>
              <span role="columnheader">
                {t('formRenderer.eSign.colPhone')}
                <Tooltip title={t('formRenderer.eSign.colPhoneHelp')}>
                  <InfoCircleOutlined className="bmo-esign-table__info" />
                </Tooltip>
              </span>
              {/* Dealer + representative code are ADVISOR-only facts, pulled
                  from the signed-in session rather than the application — the
                  shared help text says so once for both columns. */}
              <span role="columnheader">
                {t('formRenderer.eSign.colDealerCode')}
                <Tooltip title={t('formRenderer.eSign.colAdvisorCodesHelp')}>
                  <InfoCircleOutlined className="bmo-esign-table__info" />
                </Tooltip>
              </span>
              <span role="columnheader">
                {t('formRenderer.eSign.colRepCode')}
                <Tooltip title={t('formRenderer.eSign.colRepCodeHelp')}>
                  <InfoCircleOutlined className="bmo-esign-table__info" />
                </Tooltip>
              </span>
              <span role="columnheader">{t('formRenderer.eSign.colType')}</span>
            </div>
            {signers.map(({ role, instanceIdx }) => (
              <SignerRow
                key={`${role.slug}:${instanceIdx}`}
                role={role}
                instanceIdx={instanceIdx}
                onChange={onChange}
                sectionByField={sectionByField}
                values={values}
                advisorCodes={advisorCodes}
                advisorName={user?.name ?? user?.preferred_username ?? null}
                advisorEmail={user?.email ?? null}
                t={t}
                verifyResults={verifyResults}
                verifying={verifying}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ---- Sub-components --------------------------------------------------------

function SignerRow({
  role,
  instanceIdx,
  onChange,
  sectionByField,
  values,
  advisorCodes,
  advisorName,
  advisorEmail,
  t,
  verifyResults,
  verifying,
}: {
  role: SignatoryRole;
  /** 0 for a normal signer; the repeatable-section instance otherwise. */
  instanceIdx: number;
  onChange?: (key: string, next: FieldValue) => void;
  sectionByField: Map<string, FieldSectionInfo>;
  values: Record<string, FieldValue>;
  /** Dealer code + selectable rep codes for the advisor row. Client rows leave
   *  both cells empty — neither is a fact about a client signatory. */
  advisorCodes: { dealerCode: string | null; representativeCodes: string[] };
  advisorName: string | null;
  advisorEmail: string | null;
  t: (key: string, opts?: Record<string, unknown>) => string;
  /** Verify results keyed by normalized (trim+lowercase) email address. */
  verifyResults: Record<string, EmailVerifyResult>;
  verifying: boolean;
}) {
  const resolved = resolveSignerContact(
    role,
    values,
    sectionByField,
    instanceIdx,
    advisorName,
    advisorEmail,
    t,
  );

  const modeKey = esignModeKey(role.slug, instanceIdx);
  const selectedMode = readESignMode(values, role.slug, instanceIdx) ?? undefined;
  const isAdvisor = role.type === 'advisor';
  const repCodeKey = esignRepCodeKey(role.slug, instanceIdx);
  const selectedRepCode =
    readESignRepCode(values, role.slug, instanceIdx, advisorCodes.representativeCodes) ?? undefined;
  const normalizedEmail = resolved.email?.trim().toLowerCase() ?? null;
  const emailVerification = normalizedEmail ? verifyResults[normalizedEmail] : undefined;

  return (
    <div className="bmo-esign-table__row" role="row">
      {/* "Surname, Given name" per the design. resolveSignerContact returns a
          display name; the role tag and signing order move into the cell as
          secondary text so the table keeps four columns. */}
      {/* Name only — the Figma shows no role tag and no signing order. */}
      <div role="cell">
        <Text strong style={{ fontSize: 14, color: '#111827' }}>
          {resolved.fullName || t('formRenderer.eSign.unnamedSigner')}
        </Text>
      </div>
      {/* Values are resolved from the form, not typed here — the boxed styling
          matches the design without implying they're editable. */}
      {/* Verification reads as a green/red glyph pinned to the right inside
          the field, per the client's Figma — not a coloured message block
          underneath. The wording (and any "did you mean" suggestion) moves
          into the icon's tooltip so the reason is still reachable, and the
          Next-blocking behaviour is unchanged. */}
      <div role="cell">
        <div className="bmo-esign-table__value">
          <span className="bmo-esign-table__value-text">
            {resolved.email || <span className="bmo-esign-table__empty">—</span>}
          </span>
          {resolved.email && (
            <EmailVerifyIcon verifying={verifying} result={emailVerification} t={t} />
          )}
        </div>
      </div>
      <div role="cell">
        <div className="bmo-esign-table__value">
          <span className="bmo-esign-table__value-text">
            {resolved.phone || <span className="bmo-esign-table__empty">—</span>}
          </span>
        </div>
      </div>
      {/* Dealer code — read-only, straight off the advisor's GAMX profile.
          Client signatories don't have one, so their cell holds the same em
          dash an unmapped contact field would. */}
      <div role="cell">
        <div className="bmo-esign-table__value">
          <span className="bmo-esign-table__value-text">
            {(isAdvisor && advisorCodes.dealerCode) || (
              <span className="bmo-esign-table__empty">—</span>
            )}
          </span>
        </div>
      </div>
      {/* Representative code — which of the advisor's codes this application is
          filed under. Auto-selected upstream when they carry exactly one; a
          multi-code advisor picks here. Rendered as a plain cell for clients,
          and for an advisor whose session carries no codes at all (a failed
          GAMX lookup) — there is nothing to choose between. */}
      <div role="cell">
        {isAdvisor && advisorCodes.representativeCodes.length > 0 ? (
          <Select
            value={selectedRepCode}
            onChange={(next) => onChange?.(repCodeKey, next)}
            disabled={!onChange}
            placeholder={t('formRenderer.eSign.repCode.placeholder')}
            aria-label={t('formRenderer.eSign.repCode.ariaLabel', { name: resolved.fullName })}
            style={{ width: '100%' }}
            options={advisorCodes.representativeCodes.map((code) => ({
              value: code,
              label: code,
            }))}
          />
        ) : (
          <div className="bmo-esign-table__value">
            <span className="bmo-esign-table__value-text">
              <span className="bmo-esign-table__empty">—</span>
            </span>
          </div>
        )}
      </div>
      <div role="cell">
        {/* The one editable control on this page. Contact details above stay
            read-only ("non editable from the summary page"); the mode is the
            advisor's decision and is made here, per signatory. */}
        <Select
          value={selectedMode}
          onChange={(next) => onChange?.(modeKey, next)}
          disabled={!onChange}
          placeholder={t('formRenderer.eSign.mode.placeholder')}
          aria-label={t('formRenderer.eSign.mode.ariaLabel', { name: resolved.fullName })}
          style={{ width: '100%' }}
          options={[
            { value: 'remote', label: t('formRenderer.eSign.mode.remote') },
            { value: 'in-person', label: t('formRenderer.eSign.mode.inPerson') },
          ]}
        />
      </div>
    </div>
  );
}

/**
 * Verification glyph pinned to the right edge of the email field.
 *
 * The 2026-07-27 client Figma replaced the coloured message block that used
 * to sit under the address with a single green/red icon inside the input. The
 * verdict wording — and ZeroBounce's "did you mean" suggestion — moves into
 * the tooltip rather than being dropped, so an advisor can still find out WHY
 * an address failed. INFLOWS-188 blocking behaviour is untouched: an Invalid
 * result still stops Next via the step-blocker signal computed upstream.
 *
 *   - Invalid  → red exclamation
 *   - Warn     → amber warning (CatchAll / SpamTrap / DoNotMail)
 *   - Valid    → green check
 *   - Unknown  → nothing (soft-failed lookup shouldn't imply a verdict)
 *   - Checking → spinner
 */
function EmailVerifyIcon({
  verifying,
  result,
  t,
}: {
  verifying: boolean;
  result: EmailVerifyResult | undefined;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  if (!result) {
    if (!verifying) return null;
    return (
      <Tooltip
        title={t('formRenderer.eSign.emailVerify.checking', { defaultValue: 'Checking email…' })}
      >
        <LoadingOutlined className="bmo-esign-table__status" style={{ color: '#6b7280' }} />
      </Tooltip>
    );
  }

  const kind = badgeKindFor(result.status);
  if (kind === 'silent') return null;

  const palette = BADGE_PALETTE[kind];
  const Icon = palette.icon;
  const labelKey = LABEL_KEY_FOR_STATUS[result.status] ?? 'formRenderer.eSign.emailVerify.unknown';
  const label = t(labelKey, { defaultValue: DEFAULT_LABEL_FOR_STATUS[result.status] });
  const suggestion = result.didYouMean
    ? t('formRenderer.eSign.emailVerify.didYouMean', {
        suggestion: result.didYouMean,
        defaultValue: 'Did you mean {{suggestion}}?',
      })
    : null;

  return (
    <Tooltip title={suggestion ? `${label} ${suggestion}` : label}>
      <Icon
        className="bmo-esign-table__status"
        style={{ color: palette.icon_color }}
        aria-label={label}
      />
    </Tooltip>
  );
}

/** Which visual band a status renders as. `silent` means don't render the
 *  badge at all (Valid without a typo hint, Unknown from a soft-failed
 *  ZeroBounce call). */
function badgeKindFor(status: EmailVerifyStatus): 'invalid' | 'warn' | 'valid' | 'silent' {
  if (status === 'Invalid') return 'invalid';
  if (status === 'SpamTrap' || status === 'DoNotMail' || status === 'CatchAll') return 'warn';
  if (status === 'Valid') return 'valid';
  return 'silent';
}

const BADGE_PALETTE: Record<
  'invalid' | 'warn' | 'valid',
  {
    bg: string;
    border: string;
    text: string;
    icon_color: string;
    icon: typeof ExclamationCircleFilled;
  }
> = {
  invalid: {
    bg: '#fef2f2',
    border: '#fecaca',
    text: '#991b1b',
    icon_color: '#dc2626',
    icon: ExclamationCircleFilled,
  },
  warn: {
    bg: '#fefce8',
    border: '#fde68a',
    text: '#854d0e',
    icon_color: '#ca8a04',
    icon: WarningFilled,
  },
  valid: {
    bg: '#f0fdf4',
    border: '#bbf7d0',
    text: '#166534',
    icon_color: '#16a34a',
    icon: CheckCircleFilled,
  },
};

const LABEL_KEY_FOR_STATUS: Record<EmailVerifyStatus, string> = {
  Valid: 'formRenderer.eSign.emailVerify.valid',
  Invalid: 'formRenderer.eSign.emailVerify.invalid',
  CatchAll: 'formRenderer.eSign.emailVerify.catchAll',
  SpamTrap: 'formRenderer.eSign.emailVerify.spamTrap',
  DoNotMail: 'formRenderer.eSign.emailVerify.doNotMail',
  Unknown: 'formRenderer.eSign.emailVerify.unknown',
};

const DEFAULT_LABEL_FOR_STATUS: Record<EmailVerifyStatus, string> = {
  Valid: 'Email verified.',
  Invalid: 'This email is not deliverable.',
  CatchAll: "Domain accepts anything — we can't confirm the mailbox.",
  SpamTrap: 'This address looks like a spam trap.',
  DoNotMail: 'This address is on a do-not-mail list.',
  Unknown: 'Verification unavailable.',
};

function EmptyCard({ message }: { message: string }) {
  return (
    <div
      style={{
        padding: '24px 18px',
        borderRadius: 10,
        background: '#f8fafc',
        border: '1px dashed #cbd5e1',
        textAlign: 'center',
      }}
    >
      <Text type="secondary" style={{ fontSize: 13 }}>
        {message}
      </Text>
    </div>
  );
}

// ---- Helpers ---------------------------------------------------------------

/**
 * Resolve the signer's name/email/phone from the OneSpan config + form values
 * (applicant) or the BFF session (advisor). Returns a simple struct ready
 * for the SignerRow display.
 */
/** Section placement for a field — enough to build its instance-qualified
 *  value key when the owning section repeats. */
type FieldSectionInfo = { id: string; repeatable: boolean; minInstances: number };

/** fieldId -> owning section, for every authored field on the template. */
function useMemoFieldSections(template: WorkflowTemplate): Map<string, FieldSectionInfo> {
  return useMemo(() => {
    const map = new Map<string, FieldSectionInfo>();
    for (const step of template.steps) {
      if (step.type !== 'DynamicForm') continue;
      for (const section of step.sections) {
        const info: FieldSectionInfo = {
          id: section.id,
          repeatable: section.repeatable === true,
          minInstances: sectionMinInstances(section),
        };
        for (const row of section.rows) {
          for (const col of row.columns) {
            if (col.field) map.set(col.field.id, info);
          }
        }
      }
    }
    return map;
  }, [template]);
}

/**
 * How many signers one authored role currently stands for.
 *
 * A role whose mapped fields live in a repeatable section represents one
 * signer PER INSTANCE — two Accountholders means two people to sign, each
 * with their own name and email. Anything else is a single signer.
 */
export function signerInstanceCount(
  role: SignatoryRole,
  sectionByField: Map<string, FieldSectionInfo>,
  values: Record<string, FieldValue>,
): number {
  if (role.type === 'advisor') return 1;
  const m = role.fieldMappings;
  for (const id of [m?.nameFieldId, m?.lastNameFieldId, m?.emailFieldId, m?.phoneFieldId]) {
    if (!id || id === EMPTY_GUID) continue;
    const sec = sectionByField.get(id);
    if (sec?.repeatable) return getInstanceCount(values, sec.id, sec.minInstances);
  }
  return 1;
}

function resolveSignerContact(
  role: SignatoryRole,
  values: Record<string, FieldValue>,
  sectionByField: Map<string, FieldSectionInfo>,
  instanceIdx: number,
  advisorName: string | null,
  advisorEmail: string | null,
  t: (key: string) => string,
): { fullName: string; email: string | null; phone: string | null } {
  if (role.type === 'advisor') {
    return {
      fullName: advisorName || t('formRenderer.eSign.signedInAdvisor'),
      email: advisorEmail,
      phone: null,
    };
  }

  const m = role.fieldMappings;
  const pick = (id: string | undefined): string | null => {
    if (!id || id === EMPTY_GUID) return null;
    // A field inside a repeatable section stores each instance under its own
    // qualified key; a plain field is just its id.
    const sec = sectionByField.get(id);
    const v = values[sec?.repeatable ? qualifiedKey(sec.id, instanceIdx, id) : id];
    if (v === null || v === undefined) return null;
    if (typeof v === 'string' || typeof v === 'number') {
      const s = String(v).trim();
      return s ? s : null;
    }
    return null;
  };

  const name = pick(m?.nameFieldId);
  const lastName = pick(m?.lastNameFieldId);
  const fullName = [name, lastName].filter(Boolean).join(' ') || name || '';
  return {
    fullName,
    email: pick(m?.emailFieldId),
    phone: pick(m?.phoneFieldId),
  };
}
